import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import Inicio, { ModoInicio } from './Inicio.js';
import Clubes from './clubes/BaseClubes.js';
import DatosClub from './clubes/DatosClub.js';
import DatosEstadio from './estadios/DatosEstadio.js';
import DatosPersona from './personas/DatosPersona.js';
import DatosTorneo from './torneos/DatosTorneo.js';
import Estadios from './estadios/BaseEstadios.js';
import FichaPartido from './partidos/FichaPartido.js';
import Geografia from './admin/AdminGeografia.js';
import HomeTorneos from './torneos/HomeTorneos.js';
import HomeTorneosInt from './torneos/HomeTorneosInt.js';
import HomeTorneosPaises from './torneos/HomeTorneosPaises.js';
import HomeTorneosSelec from './torneos/HomeTorneosSelec.js';
import NuevoTorneo from './torneos/NuevoTorneo.js';
import Parametros from './admin/AdminParametros.js';
import Personas from './personas/BasePersonas.js';
import SeleccionesPais from './selecciones/BaseSeleccionesPais.js';
import RouterTorneos from './torneos/RouterTorneos.js';
// ...existing imports...
import { ARGENTINA } from './constantes/Paises.js';
import * as Filtros from './constantes/Filtros.js';
import * as DataSeguridad from './data/DataSeguridad.js';
import styles from './Menu.module.css';
import UserContext from '../context/UserContext.js';

const Menu = () => {
  const navigate = useNavigate();
  const [showSubmenu, setShowSubmenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [usuario, setUsuario] = useState(null);
  const [modo, setModo] = useState(ModoInicio.NORMAL);
  const [estadoBackend, setEstadoBackend] = useState(false);
  const [suppressErrors, setSuppressErrors] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const validarBackend = async () => {
      try {
        const heartbeat = await DataSeguridad.Heartbeat();
        if (!cancelled) setEstadoBackend(heartbeat);
      } catch {
        // on error assume backend not available
        if (!cancelled) setEstadoBackend(false);
      }
    };

    // run immediately, then every 30s
    validarBackend();
    const timerId = setInterval(validarBackend, 30_000);

    return () => {
      cancelled = true;
      clearInterval(timerId);
    };
  }, []);

  useEffect(() => {
    const getUsuarioLogueado = async () => {
      let user = null;
      try {
        const logueado = await DataSeguridad.Authenticated();
        if (logueado) {
          // Use GetUsuarioSafe to avoid automatic redirect on 401
          // This prevents race condition in AWS where session isn't yet propagated
          user = await DataSeguridad.GetUsuarioSafe();
        }
      } catch (error) {
        // If we can't determine auth status, silently fail
        // Inicio.js should have already fetched the user on login
        console.warn('Menu: could not fetch user during mount:', error);
        return;
      }
      if (user !== null && user !== Promise.resolve(user)) {
        setUsuario(user);
      }
    };
    if (usuario === null && estadoBackend) {
      getUsuarioLogueado();
    } else if (!estadoBackend && usuario !== null) {
      // backend went down while user was logged -> treat as session expired
      setUsuario(null);
      setModo(ModoInicio.SESION_VENCIDAD);
      navigate('/nosession');
    }
  }, [usuario, estadoBackend, navigate]);

  const handlerLogout = () => {
    setModo(ModoInicio.LOGOUT);
    navigate('/');
  };

  const UsuarioLogueadoHandler = (usuario) => {
    setUsuario(usuario);
    setModo(ModoInicio.NORMAL);
    navigate('/');
  };

  const UsuarioDeslogueadoHandler = () => {
    setUsuario(null);
    setModo(ModoInicio.NORMAL);
  };

  const toggleSubmenu = (submenuName) => {
    if (usuario !== null) {
      // Solo en mobile - en desktop se usa hover
      if (window.innerWidth <= 768) {
        setActiveSubmenu(activeSubmenu === submenuName ? null : submenuName);
      }
      // En desktop no hacer nada - los submenús se controlan solo por hover
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setActiveSubmenu(null);
  };

  const handleMobileNavClick = (e, to) => {
    handleNavClick(e, to);
    closeMobileMenu();
  };

  const handleNavClick = async (e, to) => {
    // allow modified clicks (open in new tab) to behave normally
    if (
      !e ||
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.altKey ||
      e.ctrlKey ||
      e.shiftKey
    )
      return;
    e.preventDefault();

    // validate that the user is still authenticated before navigating
    let authenticated = false;
    try {
      authenticated = await DataSeguridad.Authenticated();
    } catch {
      authenticated = false;
    }

    if (authenticated) {
      navigate(to);
    } else {
      // session expired or not authenticated -> force local logout and send to login
      if (setSuppressErrors) setSuppressErrors(true);
      try {
        UsuarioDeslogueadoHandler();
      } catch {}
      navigate('/login');
      setTimeout(() => {
        if (setSuppressErrors) setSuppressErrors(false);
      }, 2000);
    }
  };

  const mostrarSubmenu = () => {
    if (window.innerWidth > 768 && !showSubmenu && usuario !== null) {
      setShowSubmenu(true);
    }
  };

  const ocultarSubmenu = () => {
    if (window.innerWidth > 768 && showSubmenu) {
      setShowSubmenu(false);
    }
  };

  const loginContext = {
    usuario: usuario,
    estadoBackend: estadoBackend,
    UsuarioLogueadoHandler: UsuarioLogueadoHandler,
    UsuarioDeslogueadoHandler: UsuarioDeslogueadoHandler,
    suppressErrors: suppressErrors,
    setSuppressErrors: setSuppressErrors,
  };

  //console.log("Menu", modo, usuario, loginContext, estadoBackend);

  return (
    <UserContext.Provider value={loginContext}>
      <div className={`${styles.main}`}>
        {/* Mobile Header Bar */}
        <div className={`${styles.mobileHeader}`}>
          <button
            className={`${styles.hamburgerBtn}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            <span className={`${styles.hamburgerLine}`}></span>
            <span className={`${styles.hamburgerLine}`}></span>
            <span className={`${styles.hamburgerLine}`}></span>
          </button>
          <div className={`${styles.mobileHeaderContent}`}>⚽ Base de Fútbol</div>
        </div>

        <ul className={`${styles.menu} ${mobileMenuOpen ? styles.menuOpen : ''}`}>
          <li>
            <a href="/" onClick={(e) => handleMobileNavClick(e, '/')}>
              Inicio
            </a>
          </li>
          <li
            className={`${usuario === null ? styles.submenuDisabled : styles.submenu} ${activeSubmenu === 'argentina' ? styles.submenuActive : ''}`}
            onMouseEnter={mostrarSubmenu}
            onMouseLeave={ocultarSubmenu}
          >
            <button
              className={styles.menuButton}
              onClick={() => toggleSubmenu('argentina')}
              disabled={usuario === null}
            >
              Argentina
            </button>
            {(window.innerWidth <= 768 ? activeSubmenu === 'argentina' : true) && (
              <ul className={`${styles.submenuContent}`}>
                <li>
                  <a
                    href={'/torneosPais/' + ARGENTINA}
                    onClick={(e) => handleMobileNavClick(e, '/torneosPais/' + ARGENTINA)}
                  >
                    Torneos
                  </a>
                </li>
                <li>
                  <a
                    href={'/selecciones/' + ARGENTINA}
                    onClick={(e) => handleMobileNavClick(e, '/selecciones/' + ARGENTINA)}
                  >
                    Selecciones
                  </a>
                </li>
                <li>
                  <a
                    href={'/clubes?pais=' + ARGENTINA}
                    onClick={(e) => handleMobileNavClick(e, '/clubes?pais=' + ARGENTINA)}
                  >
                    Clubes
                  </a>
                </li>
                <li>
                  <a
                    href={'/enfrentamientos?pais=' + ARGENTINA}
                    onClick={(e) => handleMobileNavClick(e, '/enfrentamientos?pais=' + ARGENTINA)}
                  >
                    Enfrentamientos
                  </a>
                </li>
              </ul>
            )}
          </li>
          <li>
            <a
              className={usuario === null ? styles.disabledLink : ''}
              href="/clubes"
              onClick={(e) => handleMobileNavClick(e, '/clubes')}
            >
              Clubes
            </a>
          </li>
          <li
            className={`${styles.menuConSubmenu} ${activeSubmenu === 'personas' ? styles.submenuActive : ''}`}
            onMouseEnter={mostrarSubmenu}
            onMouseLeave={ocultarSubmenu}
          >
            <button
              className={styles.menuButton}
              onClick={() => toggleSubmenu('personas')}
              disabled={usuario === null}
            >
              Personas
            </button>
            {(window.innerWidth <= 768 ? activeSubmenu === 'personas' : true) &&
              usuario !== null && (
                <ul className={`${styles.submenuContent}`}>
                  <li>
                    <a href="/personas" onClick={(e) => handleMobileNavClick(e, '/personas')}>
                      Todos
                    </a>
                  </li>
                  <li>
                    <a href="/jugadores" onClick={(e) => handleMobileNavClick(e, '/jugadores')}>
                      Jugadores
                    </a>
                  </li>
                  <li>
                    <a href="/tecnicos" onClick={(e) => handleMobileNavClick(e, '/tecnicos')}>
                      Directores técnicos
                    </a>
                  </li>
                  <li>
                    <a href="/arbitros" onClick={(e) => handleMobileNavClick(e, '/arbitros')}>
                      Arbitros
                    </a>
                  </li>
                </ul>
              )}
          </li>
          <li
            className={`${styles.menuConSubmenu} ${activeSubmenu === 'torneos' ? styles.submenuActive : ''}`}
            onMouseEnter={mostrarSubmenu}
            onMouseLeave={ocultarSubmenu}
          >
            <button
              className={styles.menuButton}
              onClick={() => toggleSubmenu('torneos')}
              disabled={usuario === null}
            >
              Torneos
            </button>
            {(window.innerWidth <= 768 ? activeSubmenu === 'torneos' : true) &&
              usuario !== null && (
                <ul className={`${styles.submenuContent}`}>
                  <li>
                    <a href="/torneos" onClick={(e) => handleMobileNavClick(e, '/torneos')}>
                      Todos
                    </a>
                  </li>
                  <li>
                    <a
                      href="/torneos/nacionales"
                      onClick={(e) => handleMobileNavClick(e, '/torneos/nacionales')}
                    >
                      Nacionales
                    </a>
                  </li>
                  <li>
                    <a
                      href="/torneos/internacionales"
                      onClick={(e) => handleMobileNavClick(e, '/torneos/internacionales')}
                    >
                      Internacionales
                    </a>
                  </li>
                  <li>
                    <a
                      href="/torneos/selecciones"
                      onClick={(e) => handleMobileNavClick(e, '/torneos/selecciones')}
                    >
                      Selecciones
                    </a>
                  </li>
                  {usuario && usuario.puedeEditar && (
                    <li>
                      <a
                        href="/torneos/nuevo"
                        onClick={(e) => handleMobileNavClick(e, '/torneos/nuevo')}
                      >
                        Nuevo Torneo
                      </a>
                    </li>
                  )}
                </ul>
              )}
          </li>
          {usuario && usuario.puedeEditar && (
            <li
              className={`${styles.submenu} ${activeSubmenu === 'admin' ? styles.submenuActive : ''}`}
              onMouseEnter={mostrarSubmenu}
              onMouseLeave={ocultarSubmenu}
            >
              <button className={styles.menuButton} onClick={() => toggleSubmenu('admin')}>
                Administración
              </button>
              {(window.innerWidth <= 768 ? activeSubmenu === 'admin' : true) && (
                <ul className={`${styles.submenuContent}`}>
                  <li>
                    <a
                      href="/admin/geografia"
                      onClick={(e) => handleMobileNavClick(e, '/admin/geografia')}
                    >
                      Geografía
                    </a>
                  </li>
                  <li>
                    <a
                      href="/admin/estadios"
                      onClick={(e) => handleMobileNavClick(e, '/admin/estadios')}
                    >
                      Estadios
                    </a>
                  </li>
                  <li>
                    <a
                      href="/admin/parametros"
                      onClick={(e) => handleMobileNavClick(e, '/admin/parametros')}
                    >
                      Parametros
                    </a>
                  </li>
                </ul>
              )}
            </li>
          )}
          {usuario && (
            <li className={styles.logoutItem}>
              <button className={styles.logoutBtn} onClick={handlerLogout}>
                Logout
              </button>
            </li>
          )}
        </ul>
        <div className={`${styles.menuBack}`} />
        <div className={`${styles.contenedor}`}>
          <Routes>
            <Route exact path="/" element={<Inicio modo={modo} />} />
            <Route exact path="/login" element={<Inicio modo={ModoInicio.LOGIN} />} />
            <Route exact path="/logout" element={<Inicio modo={ModoInicio.LOGOUT} />} />
            <Route exact path="/nosession" element={<Inicio modo={ModoInicio.SESION_VENCIDAD} />} />
            <Route path="/clubes" element={<Clubes />} />
            <Route path="/club/:id" element={<DatosClub />} />
            <Route path="/personas" element={<Personas />} />
            <Route path="/persona/:id" element={<DatosPersona />} />
            <Route
              path="/jugadores"
              element={<Personas profesion={Filtros.PathPersona.JUGADORES} />}
            />
            <Route exact path="/jugador/:id" element={<DatosPersona />} />
            <Route
              path="/tecnicos"
              element={<Personas profesion={Filtros.PathPersona.TECNICOS} />}
            />
            <Route exact path="/tecnico/:id" element={<DatosPersona />} />
            <Route
              path="/arbitros"
              element={<Personas profesion={Filtros.PathPersona.ARBITROS} />}
            />
            <Route exact path="/arbitro/:id" element={<DatosPersona />} />
            <Route exact path="/selecciones/:id" element={<SeleccionesPais />} />
            <Route path="/torneos" element={<HomeTorneos />} />
            <Route exact path="/torneos/nuevo" element={<NuevoTorneo />} />
            <Route
              exact
              path="/torneosPais/:idPais"
              element={<RouterTorneos filtro={Filtros.Torneo.PAIS} />}
            />
            <Route
              exact
              path="/torneosInt/:codTipoTorneo"
              element={<RouterTorneos filtro={Filtros.Torneo.TIPO_TORNEO} />}
            />
            <Route
              exact
              path="/torneosPais/:idPais/division/:idDivision"
              element={<RouterTorneos filtro={Filtros.Torneo.DIVISION} />}
            />
            <Route exact path="/torneos/internacionales" element={<HomeTorneosInt />} />
            <Route exact path="/torneos/nacionales" element={<HomeTorneosPaises />} />
            <Route exact path="/torneos/selecciones" element={<HomeTorneosSelec />} />
            <Route exact path="/torneos/:idTorneo" element={<DatosTorneo />} />
            <Route
              exact
              path="/torneos/:idTorneo/equipo/:idEquipo"
              element={<RouterTorneos filtro={Filtros.Torneo.EQUIPO} />}
            />
            <Route
              exact
              path="/torneos/:idTorneo/etapa/:codEtapa"
              element={<RouterTorneos filtro={Filtros.Torneo.ETAPA} />}
            />
            <Route exact path="/partido/:idPartido" element={<FichaPartido />} />
            <Route exact path="/admin/geografia" element={<Geografia />} />
            <Route exact path="/admin/estadios" element={<Estadios />} />
            <Route exact path="/estadio/:id" element={<DatosEstadio />} />
            <Route exact path="/admin/parametros" element={<Parametros />} />
          </Routes>
        </div>
      </div>
    </UserContext.Provider>
  );
};

export default Menu;
